import type {Room} from 'matrix-js-sdk';
import type {RoomWithLastMessage} from '../../hooks/useMatrix';
import type {DirectMessageRoomOutcome} from '../../services/matrixService';

type DirectRoomLookup = {
    readonly rooms: readonly RoomWithLastMessage[];
    readonly targetUserId: string;
    readonly matrixIdToUserId: Readonly<Record<string, string>>;
    readonly isDirectRoom: (room: Room) => boolean;
};

export function findDirectRoomByUserId({
    rooms,
    targetUserId,
    matrixIdToUserId,
    isDirectRoom,
}: DirectRoomLookup): string | undefined {
    const matches = rooms.filter(({room, otherUsers}) =>
        room?.getMyMembership() === 'join' &&
        isDirectRoom(room) &&
        otherUsers.length === 1 &&
        matrixIdToUserId[otherUsers[0].userId] === targetUserId,
    );
    return (matches.find(room => room.lastMessage != null) ?? matches[0])?.roomId;
}

export const directRoomInFlight = new Map<string, Promise<string>>();

export function resetDirectRoomInFlight(): void {
    directRoomInFlight.clear();
}

export class DirectRoomUnavailableError extends Error {
    constructor(peerId: string) {
        super(`Could not confirm whether a DM with ${peerId} exists`);
        this.name = 'DirectRoomUnavailableError';
    }
}

export interface ResolveOrCreateDirectRoomDeps {
    readonly peerId: string;
    readonly lookup: () => DirectMessageRoomOutcome;
    readonly waitForOutcome: () => Promise<DirectMessageRoomOutcome>;
    readonly findLocalFallback: () => string | undefined;
    readonly createRoom: () => Promise<string>;
}

export function resolveOrCreateDirectRoom({
    peerId,
    lookup,
    waitForOutcome,
    findLocalFallback,
    createRoom,
}: ResolveOrCreateDirectRoomDeps): Promise<string> {
    const pending = directRoomInFlight.get(peerId);
    if (pending) {
        return pending;
    }

    const promise = (async () => {
        let outcome = lookup();
        if (outcome.status === 'pending') {
            outcome = await waitForOutcome();
        }

        if (outcome.status === 'found') {
            return outcome.roomId;
        }

        if (outcome.status === 'pending') {
            throw new DirectRoomUnavailableError(peerId);
        }

        return findLocalFallback() ?? await createRoom();
    })();

    directRoomInFlight.set(peerId, promise);
    promise
        .finally(() => {
            if (directRoomInFlight.get(peerId) === promise) {
                directRoomInFlight.delete(peerId);
            }
        })
        .catch(() => undefined);

    return promise;
}
